import { AppWindow, Info, Monitor } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui';
import { desktop, type DesktopSource, type ScreenPermission } from '../lib/desktop';

interface Loaded {
  permission: ScreenPermission;
  sources: DesktopSource[];
}

/**
 * Desktop app only: real thumbnails of every screen and window, like Zoom's share window.
 * Refreshed every few seconds while open so the previews stay current.
 */
export function DesktopSourcePicker({ selected, onSelect, onShare }: { selected: DesktopSource | null; onSelect(s: DesktopSource): void; onShare(s: DesktopSource): void }) {
  const [data, setData] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer = 0;
    const load = async () => {
      try {
        const next = await desktop!.listSources();
        if (!alive) return;
        setData(next);
        setFailed(false);
      } catch {
        if (alive) setFailed(true);
      }
      if (alive) timer = window.setTimeout(load, 3000);
    };
    void load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  // Keep the selection pointing at the fresh object (new thumbnail); default to the first screen.
  useEffect(() => {
    if (!data) return;
    const same = selected && data.sources.find((s) => s.id === selected.id);
    if (same && same !== selected) onSelect(same);
    else if (!same) {
      const first = data.sources.find((s) => s.kind === 'screen') ?? data.sources[0];
      if (first) onSelect(first);
    }
  }, [data, selected, onSelect]);

  if (failed && !data) {
    return <div className="callout callout-warning">No se pudo obtener la lista de pantallas y ventanas.</div>;
  }
  if (!data) {
    return <p className="hint desk-loading">Buscando pantallas y ventanas…</p>;
  }

  const screens = data.sources.filter((s) => s.kind === 'screen');
  const windows = data.sources.filter((s) => s.kind === 'window');
  const blocked = data.permission === 'denied' || data.permission === 'restricted' || (data.sources.length > 0 && data.sources.every((s) => !s.thumbnail));

  return (
    <div className="desk-picker">
      {blocked && desktop!.platform === 'darwin' && (
        <div className="callout callout-warning desk-permission">
          <Info size={16} />
          <span>
            macOS no deja a Nexo Meet ver tu pantalla. Actívalo en Ajustes del Sistema → Privacidad y seguridad → <strong>Grabación de pantalla y audio del sistema</strong>, y después cierra y vuelve a abrir Nexo Meet.
          </span>
          <Button variant="ghost" onClick={() => void desktop!.openScreenSettings()}>
            Abrir Ajustes
          </Button>
        </div>
      )}
      <Section title="Pantallas" items={screens} selected={selected} onSelect={onSelect} onShare={onShare} />
      <Section title="Ventanas" items={windows} selected={selected} onSelect={onSelect} onShare={onShare} empty="No hay otras ventanas abiertas." />
    </div>
  );
}

function Section({ title, items, selected, onSelect, onShare, empty }: { title: string; items: DesktopSource[]; selected: DesktopSource | null; onSelect(s: DesktopSource): void; onShare(s: DesktopSource): void; empty?: string }) {
  return (
    <section className="desk-section">
      <h3>{title}</h3>
      {items.length === 0 ? (
        <p className="hint">{empty ?? 'No hay pantallas disponibles.'}</p>
      ) : (
        <div className="desk-sources" role="radiogroup" aria-label={title}>
          {items.map((s) => (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={selected?.id === s.id}
              className="desk-source"
              data-source-kind={s.kind}
              title={s.name}
              onClick={() => onSelect(s)}
              onDoubleClick={() => onShare(s)}
            >
              <span className="desk-thumb">
                {s.thumbnail ? <img src={s.thumbnail} alt="" draggable={false} /> : s.kind === 'screen' ? <Monitor size={28} /> : <AppWindow size={28} />}
              </span>
              <span className="desk-name">
                {s.icon && <img src={s.icon} alt="" width={16} height={16} />}
                <span>{s.name}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
